import { C, Input, Badge } from "@coremonit/design-system";
import type { WizardData } from "../wizardStore";
import { Field, StepHeader } from "./common";
import { useState } from "react";
import type { Environment } from "@coremonit/types";
import { X } from "lucide-react";

const ENVS: { value: Environment; label: string }[] = [
  { value: "production",  label: "Production" },
  { value: "staging",     label: "Staging" },
  { value: "uat",         label: "UAT" },
  { value: "development", label: "Development" },
];

interface Props { data: WizardData; update: (p: Partial<WizardData>) => void }

export function Step1Details({ data, update }: Props) {
  const [tagDraft, setTagDraft] = useState("");
  return (
    <div>
      <StepHeader title="Service details" description="Identify the service so it's discoverable, groupable, and routable to the right team." />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Field label="Service name *" hint="Lowercase, hyphens, must be unique.">
          <Input value={data.name} onChange={(v) => update({ name: v })} placeholder="e.g. payment-service" size="md" />
        </Field>
        <Field label="Version *">
          <Input value={data.version} onChange={(v) => update({ version: v })} placeholder="1.0.0" size="md" />
        </Field>
        <Field label="Owner *" hint="Individual on-call reference.">
          <Input value={data.owner} onChange={(v) => update({ owner: v })} placeholder="alice.wong" size="md" />
        </Field>
        <Field label="Team *">
          <Input value={data.team} onChange={(v) => update({ team: v })} placeholder="payments" size="md" />
        </Field>
        <Field label="Language">
          <Input value={data.language} onChange={(v) => update({ language: v })} placeholder="Go" size="md" />
        </Field>
        <Field label="Runtime">
          <Input value={data.runtime} onChange={(v) => update({ runtime: v })} placeholder="Go 1.22" size="md" />
        </Field>
        <Field label="Environment">
          <select
            value={data.environment}
            onChange={(e) => update({ environment: e.target.value as Environment })}
            style={{ width: "100%", padding: "10px 12px", background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 12, color: C.text, fontSize: 13 }}
          >
            {ENVS.map((e) => <option key={e.value} value={e.value} style={{ background: C.surface }}>{e.label}</option>)}
          </select>
        </Field>
        <Field label="Description">
          <Input value={data.description} onChange={(v) => update({ description: v })} placeholder="Handles PCI-scoped payment intents" size="md" />
        </Field>
      </div>

      <div style={{ marginTop: 14 }}>
        <Field label="Tags" hint="Tab or enter to add.">
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 12, padding: 8, minHeight: 42 }}>
            {data.tags.map((t) => (
              <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 6, background: C.blueDim, color: C.blue, border: `1px solid ${C.borderBright}`, fontSize: 10, fontWeight: 700 }}>
                {t}
                <button
                  onClick={() => update({ tags: data.tags.filter((x) => x !== t) })}
                  style={{ background: "transparent", border: "none", color: C.blue, cursor: "pointer", padding: 0, display: "flex" }}
                >
                  <X size={10} />
                </button>
              </span>
            ))}
            <input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === "Tab") && tagDraft.trim()) {
                  e.preventDefault();
                  if (!data.tags.includes(tagDraft.trim())) update({ tags: [...data.tags, tagDraft.trim()] });
                  setTagDraft("");
                }
              }}
              placeholder={data.tags.length ? "" : "e.g. critical, pci, core"}
              style={{ background: "transparent", border: "none", outline: "none", color: C.text, fontSize: 12, flex: 1, minWidth: 100 }}
            />
          </div>
        </Field>
      </div>
    </div>
  );
}
